import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Search, RefreshCw, Settings, Users, Route as RouteIcon, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { listOperations, toggleOperation } from '@/services/operationsManagerApi';

const PAGE_SIZE = 8;

const OperationsManager = () => {
  const navigate = useNavigate();
  const [modules, setModules] = useState([]);
  const [totals, setTotals] = useState({ total_modules: 0, total_operations: 0 });
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);      // full reload (page 1 / search)
  const [loadingMore, setLoadingMore] = useState(false);
  const [search, setSearch] = useState('');
  const sentinelRef = useRef(null);

  // Fetch a page. reset=true replaces list; otherwise appends.
  const fetchPage = useCallback(async (pageNum, searchTerm, reset) => {
    if (reset) setLoading(true); else setLoadingMore(true);
    try {
      const data = await listOperations({ page: pageNum, pageSize: PAGE_SIZE, search: searchTerm });
      setTotals({ total_modules: data.total_modules || 0, total_operations: data.total_operations || 0 });
      setHasMore(!!data.has_more);
      setPage(data.page || pageNum);
      setModules((prev) => reset ? (data.modules || []) : [...prev, ...(data.modules || [])]);
    } catch (error) {
      console.error('Error loading operations:', error);
      toast.error('Failed to load operations');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, []);

  // Debounced search → reload from page 1
  useEffect(() => {
    const t = setTimeout(() => fetchPage(1, search, true), 300);
    return () => clearTimeout(t);
  }, [search, fetchPage]);

  const loadMore = useCallback(() => {
    if (hasMore && !loading && !loadingMore) fetchPage(page + 1, search, false);
  }, [hasMore, loading, loadingMore, page, search, fetchPage]);

  // Infinite scroll (lazy load) via IntersectionObserver
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const obs = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) loadMore();
    }, { rootMargin: '200px' });
    obs.observe(el);
    return () => obs.disconnect();
  }, [loadMore]);

  const handleToggle = async (moduleId, opKey, field, value) => {
    setModules((prev) => prev.map((m) => m.module_id !== moduleId ? m : {
      ...m,
      operations: m.operations.map((o) => o.operation_key === opKey ? { ...o, [field]: value } : o),
    }));
    try {
      await toggleOperation(opKey, field, value);
    } catch (error) {
      toast.error('Failed to save. Reverting.');
      setModules((prev) => prev.map((m) => m.module_id !== moduleId ? m : {
        ...m,
        operations: m.operations.map((o) => o.operation_key === opKey ? { ...o, [field]: !value } : o),
      }));
    }
  };

  return (
    <div className="min-h-screen bg-[#FFFBF5]">
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate('/admin/dashboard')}
              className="text-[#F9FAFB] hover:bg-white/10" data-testid="back-button">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-2">
              <Settings className="w-6 h-6" />
              <div>
                <h1 className="text-xl font-bold font-display">Operations Manager</h1>
                <p className="text-xs text-[#E8DCC8]">Control where each operation appears</p>
              </div>
            </div>
          </div>
          <Button onClick={() => fetchPage(1, search, true)} variant="secondary" size="sm" data-testid="refresh-btn">
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 md:px-6 py-6 space-y-5">
        <div className="bg-white rounded-lg border border-[#E5DFD0] p-4">
          <p className="text-sm text-[#5A5F6E] mb-3">
            Toggle each operation for <strong>User Management</strong> (available in the user permission picker)
            and the <strong>USN/Bundle Tracker</strong> (shown in the timeline). New operations appear here automatically.
          </p>
          <div className="flex items-center gap-4 flex-wrap">
            <div className="relative flex-1 min-w-[240px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search operation or module..."
                className="pl-9"
                data-testid="search-input"
              />
            </div>
            <div className="flex gap-2">
              <Badge variant="secondary" data-testid="modules-count">{totals.total_modules} modules</Badge>
              <Badge variant="secondary" data-testid="operations-count">{totals.total_operations} operations</Badge>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20 text-[#5A5F6E]">
            <Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading operations...
          </div>
        ) : modules.length === 0 ? (
          <div className="bg-white rounded-lg border border-dashed border-[#E5DFD0] p-12 text-center text-[#5A5F6E]">
            No operations match your search.
          </div>
        ) : (
          <>
            {modules.map((m) => (
              <div key={m.module_id} className="bg-white rounded-lg border border-[#E5DFD0] overflow-hidden"
                data-testid={`module-group-${m.module_id}`}>
                <div className="px-4 py-3 bg-[#F5F1E8] border-b border-[#E5DFD0] flex items-center justify-between">
                  <h2 className="font-semibold text-[#1A1F2E]">{m.module_name}</h2>
                  <span className="text-xs text-[#5A5F6E]">{m.operations.length} ops</span>
                </div>
                <div className="hidden sm:flex items-center px-4 py-2 text-xs font-medium text-[#5A5F6E] border-b border-[#F0EBE0]">
                  <div className="flex-1">Operation</div>
                  <div className="w-40 flex items-center gap-1.5 justify-center"><Users className="w-3.5 h-3.5" /> User Management</div>
                  <div className="w-40 flex items-center gap-1.5 justify-center"><RouteIcon className="w-3.5 h-3.5" /> USN/Bundle Tracker</div>
                </div>
                <div className="divide-y divide-[#F0EBE0]">
                  {m.operations.map((o) => (
                    <div key={o.operation_key} className="flex flex-col sm:flex-row sm:items-center px-4 py-3 gap-3"
                      data-testid={`op-row-${o.operation_key}`}>
                      <div className="flex-1">
                        <p className="text-sm font-medium text-[#1A1F2E]">{o.op_name || o.op_id}</p>
                        <p className="text-xs text-gray-400 font-mono">{o.op_id}</p>
                      </div>
                      <div className="sm:w-40 flex items-center sm:justify-center gap-2">
                        <span className="sm:hidden text-xs text-[#5A5F6E]">User Management</span>
                        <Switch
                          checked={o.show_in_user_management}
                          onCheckedChange={(v) => handleToggle(m.module_id, o.operation_key, 'show_in_user_management', v)}
                          data-testid={`toggle-um-${o.operation_key}`}
                        />
                      </div>
                      <div className="sm:w-40 flex items-center sm:justify-center gap-2">
                        <span className="sm:hidden text-xs text-[#5A5F6E]">USN/Bundle Tracker</span>
                        <Switch
                          checked={o.show_in_tracker}
                          onCheckedChange={(v) => handleToggle(m.module_id, o.operation_key, 'show_in_tracker', v)}
                          data-testid={`toggle-tracker-${o.operation_key}`}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}

            {/* Lazy-load sentinel + manual fallback */}
            <div ref={sentinelRef} />
            {hasMore && (
              <div className="flex justify-center py-2">
                <Button variant="outline" onClick={loadMore} disabled={loadingMore} data-testid="load-more-btn">
                  {loadingMore ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                  Load more modules
                </Button>
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
};

export default OperationsManager;
